import { forwardRef, type ReactNode, type Ref } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Loader.css';

export interface LoaderProps extends BaseProps {
  /** Visual variant. */
  variant?: 'oval' | 'bars' | 'dots';
  /** Size token or px. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
}

const SIZE_PX: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number> = { xs: 16, sm: 22, md: 28, lg: 36, xl: 44 };

function dimOf(size: LoaderProps['size']): number {
  if (size === undefined) return SIZE_PX.md;
  if (typeof size === 'number') return size;
  return SIZE_PX[size as keyof typeof SIZE_PX] ?? SIZE_PX.md;
}

/** Loading spinner: oval, three-bar, or three-dot variant. */
export const Loader = forwardRef(function Loader(
  { variant = 'oval', size = 'md', color, ...rest }: LoaderProps,
  ref: Ref<HTMLSpanElement>
): ReactNode {
  const base = useBaseAttrs('rr-loader', rest, [`rr-loader--${variant}`]);
  const { dom } = splitBaseProps(rest);
  const px = dimOf(size);
  const stroke = color ?? 'currentColor';

  return (
    <span {...dom}
      ref={ref}
      role="status"
      aria-label={typeof rest['aria-label'] === 'string' ? rest['aria-label'] : 'Loading'}
      {...base.attrs}
      className={base.className}
      style={{ ...base.style, width: px, height: px, color: stroke }}
    >
      {variant === 'oval' && (
        <svg viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
          <g fill="none" fillRule="evenodd">
            <g transform="translate(1 1)" strokeWidth="2">
              <circle strokeOpacity=".25" cx="18" cy="18" r="18" />
              <path d="M36 18c0-9.94-8.06-18-18-18">
                <animateTransform attributeName="transform" type="rotate" from="0 18 18" to="360 18 18" dur="0.8s" repeatCount="indefinite" />
              </path>
            </g>
          </g>
        </svg>
      )}
      {variant === 'bars' && (
        <span className="rr-loader__bars">
          <span /><span /><span />
        </span>
      )}
      {variant === 'dots' && (
        <span className="rr-loader__dots">
          <span /><span /><span />
        </span>
      )}
    </span>
  );
});
